import { useMemo } from "react";
import { graphql } from "gatsby";
import styles from "./my-page-block.module.less";
import { RichTextType, SimplifyGatsbyQuery, useCartContext } from "@parksandresorts/core";
import { GlobalContextConsumer } from "components/stores/global-context";
import MyParks from "@parksandresorts/myparks";
import { ICustomTexts } from "@parksandresorts/myparks/dist/helpers/host";
import { ILanguage, IParkTheme } from "@parksandresorts/myparks/dist/types/my-parks-props-types";
import SiteConfigurationStore from "components/stores/site-configuration-store";

const URL = process.env.GATSBY_MY_PARKS_URL || "https://prs-cdp-dev-myparks-webapp.azurewebsites.net/api";
const COMPANY = process.env.GATSBY_MY_PARKS_COMPANY || "TestGLT";

// This is a workaround for converting an array of 4 elements to a tuple of 4 elements, which is needed to solve a TypeScript error in MyParks.
type ToFourTuple<T> = T extends [infer A, infer B, infer C, infer D] ? [A, B, C, D] : never;

const calendarLoaderIcons = {
  firstImage: "/loader-images/bergochdalbana.svg",
  secondImage: "/loader-images/glass.svg",
  thirdImage: "/loader-images/pariserhjul.svg",
};

const serviceLayerApiBaseUrl = process.env.GATSBY_PRS_API_URL
  ? process.env.GATSBY_PRS_API_URL
  : "https://prs-cdp-dev-webapiproxy.azurewebsites.net/api/glt/";

type Props = SimplifyGatsbyQuery<Queries.MyPageBlockFragment> & {
  isShowingTopBanner: boolean; //This comes from site-blocks
  node_locale: ILanguage;
  theme: IParkTheme;
  bookingCompletedRichText: RichTextType;
};

const MyPageBlock = (props: Props) => {
  const cartContext = useCartContext();

  const labelsOverride = useMemo(
    () => new Map(props.labelOverrides?.list?.map(({ key, value }) => [key, value]) ?? []),
    [props.labelOverrides],
  );

  const gamificationRecapPins = useMemo(
    () => props.gamificationRecapPins?.map((i) => i.gatsbyImageData) ?? [],
    [props.gamificationRecapPins],
  );

  const customTexts: ICustomTexts = {
    GENERAL_BOOKING_CONDITIONS: props.generalBookingConditions,
    BOOKING_COMPLETED_RICH_TEXT: props.bookingCompletedRichText,
    pageIntroParagraph: props.discountPageIntroParagraph,
    activeDiscountsParagraph: props.discountPageActiveDiscountsParagraph,
    upsellParagraph: props.discountPageUpsellParagraph,
    usedDiscountsParagraph: props.discountPageUsedParagraph,
    nonActiveDiscountsParagraph: props.discountPageNonActiveParagraph,
    entryTicketCardParagraph: props.discountPageEntryTicketCardParagraph,
    seasonPassCardParagraph: props.discountPageSeasonPassCardParagraph,
  };

  return (
    <GlobalContextConsumer>
      {(globalContext) => (
        <section
          className={[
            styles.myPageBlock,
            globalContext.isTopNavigationHidden ? styles.noPaddingTop : "",
            props.isShowingTopBanner && !globalContext.isTopNavigationHidden ? styles.extraPaddingTop : null,
          ].join(" ")}
          data-contentful-id={props.contentful_id}
        >
          <MyParks
            language={props.node_locale || "sv"}
            parkTheme={props.theme || "gronalund"}
            myParksApiUrl={URL}
            myParksCompanyId={COMPANY}
            debug={false}
            parkLogo={props.generalParkLogo?.file?.url || "/images/my-pages/kolmarden-logo.svg"}
            calendarLoaderIcons={calendarLoaderIcons}
            generalBookingConditionsLink={
              props.generalBookingConditionsLink?.internalLink?.slug ?? props.generalBookingConditionsLink?.externalUrl
            }
            globalContext={globalContext}
            serviceLayerApiBaseUrl={serviceLayerApiBaseUrl}
            discountPageImages={props.discountsParagraphParkLogos ?? []}
            labelsOverride={labelsOverride}
            featureConfig={{
              enableParking: false,
              enableSharingProducts: false,
              enableWallet: true,
              enableDiscountsTickets: props.enableDiscountsTickets ?? false,
              enableDiscountsSeasonPasses: props.enableDiscountsSeasonPasses ?? false,
              showGLTBlackWeekInformation: SiteConfigurationStore.siteConfiguration.enableBlackWeekOffer,
            }}
            customTexts={customTexts}
            cartContext={cartContext}
            topMenuParkLogo={props.topMenuParkLogo?.file?.url ?? undefined}
            startPageUpsellCards={props.startPageUpsellCards ?? []}
            seasonPassPageUpsellCards={props.seasonPassPageUpsellCards ?? []}
            handleTicketPageUpsellCards={props.handleTicketPageUpsellCards ?? []}
            orderHistoryPageUpsellCards={props.orderHistoryPageUpsellCards ?? []}
            personDetailsPageUpsellCards={props.personDetailsPageUpsellCards ?? []}
            bookingCompletedPageUpsellCards={props.bookingCompletedPageUpsellCards ?? []}
            discountPageUpsellCards={props.discountPageUpsellCards ?? []}
            gamificationHeroImage={props.gamificationHero?.gatsbyImageData}
            gamificationRecapPins={gamificationRecapPins as ToFourTuple<typeof gamificationRecapPins>}
          ></MyParks>
        </section>
      )}
    </GlobalContextConsumer>
  );
};

export default MyPageBlock;

export const fragmentUpsellCard = graphql`
  fragment UpsellCard on ContentfulGeneralContentBlock {
    title
    shortDescriptionInList
    imageInList {
      file {
        url
      }
    }
    link {
      title
      internalLink {
        slug
      }
      externalUrl
      openInNewTab
    }
  }
`;

export const fragmentMyPageBlock = graphql`
  fragment MyPageBlock on ContentfulMyPageBlock {
    contentful_id
    id
    node_locale
    labelOverrides {
      list {
        key
        value
      }
    }
    theme
    generalParkLogo {
      file {
        url
      }
    }
    topMenuParkLogo {
      file {
        url
      }
    }
    generalBookingConditionsLink {
      ...Link
    }
    generalBookingConditions {
      raw
    }
    bookingCompletedRichText {
      raw
      references {
        __typename
        ...Link
        ...Asset
      }
    }
    startPageUpsellCards {
      ...UpsellCard
    }
    seasonPassPageUpsellCards {
      ...UpsellCard
    }
    handleTicketPageUpsellCards {
      ...UpsellCard
    }
    orderHistoryPageUpsellCards {
      ...UpsellCard
    }
    personDetailsPageUpsellCards {
      ...UpsellCard
    }
    bookingCompletedPageUpsellCards {
      ...UpsellCard
    }
    gamificationHero {
      gatsbyImageData(placeholder: BLURRED)
    }
    gamificationRecapPins {
      gatsbyImageData(height: 180, placeholder: BLURRED)
    }
    enableDiscountsTickets
    enableDiscountsSeasonPasses
    discountsParagraphParkLogos {
      gatsbyImageData(height: 60)
    }
    discountPageUpsellCards {
      ...UpsellCard
    }
    discountPageIntroParagraph {
      raw
    }
    discountPageActiveDiscountsParagraph {
      raw
    }
    discountPageUpsellParagraph {
      raw
    }
    discountPageUsedParagraph {
      raw
    }
    discountPageNonActiveParagraph {
      raw
    }
    discountPageEntryTicketCardParagraph {
      raw
    }
    discountPageSeasonPassCardParagraph {
      raw
    }
  }
`;
